/* ──────────────────────────────────────────────────────────────────────────
 * Valuation Studio desk — embedded Business Valuation (BV Engine) workspace
 * for the IPO Navigator "Valuation Studio" screen.
 *
 * It reuses the SAME backend (`/api/bv-projects`) and the SAME deterministic
 * calc engine (`lib/bv_engine.js` + `lib/bv_input_schema.js`) as the standalone
 * `/valuation` flow — only the UI is re-skinned in the IPO Navigator look
 * (render inside a `.ipo-nav-root` ancestor; scoped CSS `.ipo-nav-root .vs-*`).
 *
 * Workflow (identical to /valuation):
 *   projects  → "Start a new Business Valuation" popup (company + sector)
 *   inputs    → company header, FY labels, P&L / Balance Sheet / DCF Assumptions
 *   engine    → DCF · NAV · Comparable Company in parallel + weighted summary
 *
 * A single user may keep at most 3 BV projects (enforced backend + frontend).
 * ────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import jsPDF from "jspdf";
import {
  PL_ROWS, BS_ROWS, ASSUMPTION_ROWS,
  computeBSColumns, computePLColumns, autoSuggestAssumptions, bvProjectToEngineInputs,
} from "@/lib/bv_input_schema";
import {
  calculateDCF, calculateNAV, calculateComparable, weightedSummary, fmtL,
} from "@/lib/bv_engine";
import { SECTOR_PEER_MULTIPLES, UNLISTED_DISCOUNT, buildPeerSet } from "@/data/sector_peer_multiples";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_URL}/api`, withCredentials: true });
const MAX_PROJECTS = 3;
const fmtPct = (v) => (v == null ? "—" : `${(v * 100).toFixed(2)}%`);
const DISCLAIMER = "Disclaimer: Outputs are indicative. Validate with your CA / merchant banker before any transaction. IPOLabs, takes no responsibility for input, output or formulas used or the end results.";

/* ════════════════════════════════════════════════════════════════════════
 * Create-project popup (re-skinned in IPO Navigator light theme)
 * ════════════════════════════════════════════════════════════════════════ */
const CreateModal = ({ onClose, onCreated }) => {
  const [companyName, setCompanyName] = useState("");
  const [sectorId, setSectorId] = useState(SECTOR_PEER_MULTIPLES[0].id);
  const [creating, setCreating] = useState(false);
  const sector = SECTOR_PEER_MULTIPLES.find((s) => s.id === sectorId) || SECTOR_PEER_MULTIPLES[0];

  const create = async () => {
    setCreating(true);
    try {
      const res = await api.post("/bv-projects", {
        company_name: companyName.trim() || "Untitled BV Project",
        engine_config: {
          sector_id: sector.id,
          ev_ebitda_multiple: sector.ev_ebitda,
          ev_revenue_multiple: sector.ev_revenue,
          pe_multiple: sector.pe,
        },
      });
      onCreated(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not create project";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="vs-modal-overlay" data-testid="vs-create-modal">
      <div className="vs-modal">
        <div className="vs-modal-head">
          <div className="vs-modal-title">Start a new Business Valuation</div>
          <button className="rf-quiz-x" onClick={onClose} data-testid="vs-create-close"><i className="ti ti-x" aria-hidden="true" /></button>
        </div>
        <div className="vs-modal-body">
          <p className="vs-tagline" style={{ marginBottom: 14 }}>
            Pick the sector that best matches your company. We'll pre-load the comparable peer set
            (3 Large Cap + 1 Mid Cap + Broad NIFTY-500 sector median) and the corresponding
            multiples. You can change everything later.
          </p>
          <div className="ra-field">
            <label className="ra-label">Company name (optional)</label>
            <div className="ra-input-wrap"><input className="ra-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="e.g., Acme Industries Pvt Ltd" data-testid="vs-create-company" /></div>
          </div>
          <div className="ra-field" style={{ marginTop: 10 }}>
            <label className="ra-label">Sector</label>
            <select className="ra-input" value={sectorId} onChange={(e) => setSectorId(e.target.value)} data-testid="vs-create-sector">
              {SECTOR_PEER_MULTIPLES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="vs-peer-preview">
            <strong>Peer set:</strong>{" "}
            <span style={{ color: "#0F6E56" }}>{(sector.large_caps || []).join(", ")}</span>
            {" · "}
            <span style={{ color: "#854F0B" }}>{(sector.mid_caps || []).join(", ")}</span>
            {" · "}
            <span style={{ color: "#3C3489" }}>{sector.nifty500_median_label}</span>
            <div style={{ marginTop: 6 }}>
              <strong>Multiples:</strong> EV/EBITDA {sector.ev_ebitda}x · EV/Revenue {sector.ev_revenue}x · P/E {sector.pe}x
            </div>
          </div>
        </div>
        <div className="vs-modal-foot">
          <button className="f-btn" onClick={onClose} disabled={creating}>Cancel</button>
          <button className="f-btn solid" onClick={create} disabled={creating} data-testid="vs-create-confirm">
            {creating ? "Creating…" : "Create Project"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════
 * Projects landing
 * ════════════════════════════════════════════════════════════════════════ */
const ProjectsView = ({ projects, loading, onOpenCreate, onOpenInputs, onOpenEngine, onDelete }) => {
  const atCap = projects.length >= MAX_PROJECTS;
  return (
    <div data-testid="vs-projects-view">
      <div className="vs-grid">
        {/* Create tile */}
        <div
          className={`vs-pcard vs-pcard-create ${atCap ? "disabled" : ""}`}
          onClick={() => !atCap && onOpenCreate()}
          data-testid="vs-create-tile"
        >
          <div className="vs-pic"><i className="ti ti-plus" aria-hidden="true" /></div>
          <div className="vs-ptitle">Create New BV Project</div>
          <div className="vs-pmeta" style={{ display: "block" }}>
            {atCap
              ? <span style={{ color: "#A32D2D" }}>You've reached the maximum of {MAX_PROJECTS} projects. Delete one to create another.</span>
              : "Start a fresh business valuation. We'll create a blank input sheet for your P&L, Balance Sheet and DCF assumptions."}
          </div>
          {!atCap && <div className="vs-plink" style={{ marginTop: "auto" }}>Start Now <i className="ti ti-arrow-right" aria-hidden="true" /></div>}
        </div>

        {loading ? (
          <div className="vs-pcard"><div className="vs-pmeta">Loading projects…</div></div>
        ) : projects.length === 0 ? (
          <div className="vs-pcard" style={{ justifyContent: "center", alignItems: "center", textAlign: "center" }}>
            <i className="ti ti-chart-bar" style={{ fontSize: 26, color: "var(--color-text-tertiary)", marginBottom: 8 }} aria-hidden="true" />
            <div className="vs-pmeta" style={{ display: "block" }}>No projects yet. Click <strong>Create New BV Project</strong> to begin.</div>
          </div>
        ) : (
          projects.map((p) => (
            <div className="vs-pcard" key={p.project_id} data-testid={`vs-card-${p.project_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div className="vs-pic" style={{ background: "#EEEDFE", color: "#3C3489" }}><i className="ti ti-building-bank" aria-hidden="true" /></div>
                <button className="vs-pdel" onClick={() => onDelete(p)} data-testid={`vs-delete-${p.project_id}`} aria-label="Delete project"><i className="ti ti-trash" aria-hidden="true" /></button>
              </div>
              <div className="vs-ptitle" data-testid={`vs-proj-title-${p.project_id}`}>{p.company_name || "Untitled BV Project"}</div>
              <div className="vs-pmeta">
                {p.website && <span>{p.website}</span>}
                {p.plan_for_ipo === "yes" && <span style={{ color: "#0F6E56" }}>IPO{p.ipo_timeline ? ` · ${p.ipo_timeline}` : ""}</span>}
                <span>Updated {new Date(p.updated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
              </div>
              <div className="vs-pfoot">
                <button className="vs-plink" style={{ color: "var(--color-text-secondary)" }} onClick={() => onOpenInputs(p.project_id)} data-testid={`vs-open-inputs-${p.project_id}`}>Edit Inputs</button>
                <button className="vs-plink" onClick={() => onOpenEngine(p.project_id)} data-testid={`vs-open-engine-${p.project_id}`}>Open Engine <i className="ti ti-arrow-right" aria-hidden="true" /></button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="vs-disclaimer" data-testid="vs-disclaimer">
        <i className="ti ti-shield" style={{ fontSize: 14, color: "#BA7517" }} aria-hidden="true" />
        <div className="t">{DISCLAIMER}</div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════
 * Inputs view (P&L / Balance Sheet / DCF Assumptions) — auto-saving
 * ════════════════════════════════════════════════════════════════════════ */
const FinancialTable = ({ rows, raw, computed, onChange, fyLabels, testidPrefix }) => {
  const renderCell = (row, fy) => {
    if (row.type === "computed") {
      const val = computed?.[fy]?.[row.id];
      return (
        <div className={`vs-computed ${row.bold ? "bold" : ""}`} data-testid={`${testidPrefix}-${row.id}-${fy}`}>
          {row.format === "pct" ? fmtPct(val) : fmtL(val)}
        </div>
      );
    }
    return (
      <input type="number" step="any" className="vs-cell" placeholder="0"
        value={raw?.[row.id]?.[fy] ?? ""}
        onChange={(e) => onChange(row.id, fy, e.target.value === "" ? 0 : Number(e.target.value))}
        data-testid={`${testidPrefix}-${row.id}-${fy}`} />
    );
  };
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="vs-fin-table">
        <thead>
          <tr>
            <th style={{ width: "46%" }}>Line Item</th>
            <th>{fyLabels[2]}</th><th>{fyLabels[1]}</th><th>{fyLabels[0]}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            if (row.type === "header") return <tr key={row.id} className="vs-fin-head"><td colSpan={4}>{row.label}</td></tr>;
            if (row.type === "subheader") return <tr key={row.id} className="vs-fin-sub"><td colSpan={4}>{row.label}</td></tr>;
            return (
              <tr key={row.id} className={`vs-fin-row ${row.bold ? "bold" : ""}`}>
                <td className="vs-fin-label">{row.label}{row.type === "computed" && <span className="vs-auto-tag">auto</span>}</td>
                <td>{renderCell(row, "fy2")}</td>
                <td>{renderCell(row, "fy1")}</td>
                <td>{renderCell(row, "fy0")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const InputsView = ({ projectId, onBack, onOpenEngine }) => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("pl");
  const saveTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/bv-projects/${projectId}`);
        setProject(res.data);
      } catch { toast.error("Could not load project"); onBack(); }
      finally { setLoading(false); }
    })();
  }, [projectId]); // eslint-disable-line

  // Debounced auto-save
  useEffect(() => {
    if (!project || loading) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.put(`/bv-projects/${projectId}`, {
          company_name: project.company_name, website: project.website,
          plan_for_ipo: project.plan_for_ipo, ipo_timeline: project.ipo_timeline,
          fy_labels: project.fy_labels, pl: project.pl, bs: project.bs,
          assumptions: project.assumptions, engine_config: project.engine_config,
        });
      } catch { /* silent */ } finally { setSaving(false); }
    }, 800);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [project]); // eslint-disable-line

  const setField = (k, v) => setProject((p) => ({ ...p, [k]: v }));
  const setFy = (idx, v) => setProject((p) => { const arr = [...(p.fy_labels || ["FY 2023-24", "FY 2024-25", "FY 2025-26"])]; arr[idx] = v; return { ...p, fy_labels: arr }; });
  const setPlCell = (id, fy, v) => setProject((p) => ({ ...p, pl: { ...p.pl, [id]: { ...(p.pl?.[id] || {}), [fy]: v } } }));
  const setBsCell = (id, fy, v) => setProject((p) => ({ ...p, bs: { ...p.bs, [id]: { ...(p.bs?.[id] || {}), [fy]: v } } }));
  const setAssumption = (id, v) => setProject((p) => ({ ...p, assumptions: { ...(p.assumptions || {}), [id]: v } }));

  const bsComputed = useMemo(() => (project ? computeBSColumns(project.bs || {}) : null), [project]);
  const plComputed = useMemo(() => (project ? computePLColumns(project.pl || {}, bsComputed || {}) : null), [project, bsComputed]);
  const auto = useMemo(() => ((plComputed && bsComputed) ? autoSuggestAssumptions(plComputed, bsComputed) : null), [plComputed, bsComputed]);

  const saveAndOpen = async () => {
    setSaving(true);
    try { await api.put(`/bv-projects/${projectId}`, project); toast.success("Inputs saved"); onOpenEngine(projectId); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  if (loading || !project) return <div className="vs-pmeta" style={{ padding: 20 }}>Loading…</div>;
  const fyLabels = project.fy_labels || ["FY 2023-24", "FY 2024-25", "FY 2025-26"];

  return (
    <div data-testid="vs-inputs-view">
      <div className="vs-subbar">
        <button className="f-btn" onClick={onBack} data-testid="vs-inputs-back"><i className="ti ti-arrow-left" aria-hidden="true" /> Projects</button>
        <div className="vs-subtitle">{project.company_name || "Untitled BV Project"} — Input Sheet <span>· all figures in ₹ Lacs</span></div>
        <div className="vs-save-state">{saving ? "Saving…" : "✓ Auto-saved"}</div>
      </div>

      {/* Company header */}
      <div className="ra-card">
        <div className="ra-grid-2" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
          <div className="ra-field"><label className="ra-label">Company Name</label><div className="ra-input-wrap"><input className="ra-input" value={project.company_name || ""} onChange={(e) => setField("company_name", e.target.value)} data-testid="vs-input-company" /></div></div>
          <div className="ra-field"><label className="ra-label">Website</label><div className="ra-input-wrap"><input className="ra-input" value={project.website || ""} onChange={(e) => setField("website", e.target.value)} placeholder="https://" data-testid="vs-input-website" /></div></div>
          <div className="ra-field"><label className="ra-label">Plan for IPO?</label>
            <select className="ra-input" value={project.plan_for_ipo || ""} onChange={(e) => setField("plan_for_ipo", e.target.value || null)} data-testid="vs-input-plan-ipo">
              <option value="">— select —</option><option value="yes">Yes</option><option value="no">No</option>
            </select>
          </div>
          <div className="ra-field"><label className="ra-label">IPO Timeline</label>
            <select className="ra-input" value={project.ipo_timeline || ""} disabled={project.plan_for_ipo !== "yes"} onChange={(e) => setField("ipo_timeline", e.target.value || null)} data-testid="vs-input-ipo-timeline">
              <option value="">—</option><option value="12m">Within 12 months</option><option value="18m">Within 18 months</option><option value="beyond">Beyond 18 months</option>
            </select>
          </div>
        </div>
      </div>

      {/* FY labels */}
      <div className="ra-card">
        <div className="ra-card-h"><i className="ti ti-file-text" aria-hidden="true" /> Financial Year Labels <span className="ra-card-sub">latest → oldest, left to right</span></div>
        <div className="ra-grid-3">
          {[2, 1, 0].map((idx) => <div key={idx} className="ra-field"><div className="ra-input-wrap"><input className="ra-input" value={fyLabels[idx]} onChange={(e) => setFy(idx, e.target.value)} data-testid={`vs-fy-${idx}`} /></div></div>)}
        </div>
      </div>

      {/* Tabs */}
      <div className="vs-tabs" data-testid="vs-input-tabs">
        <button className={`vs-tab ${tab === "pl" ? "on" : ""}`} onClick={() => setTab("pl")} data-testid="vs-tab-pl">P&amp;L (Income Statement)</button>
        <button className={`vs-tab ${tab === "bs" ? "on" : ""}`} onClick={() => setTab("bs")} data-testid="vs-tab-bs">Balance Sheet</button>
        <button className={`vs-tab ${tab === "assumptions" ? "on" : ""}`} onClick={() => setTab("assumptions")} data-testid="vs-tab-assumptions">DCF Assumptions</button>
      </div>

      {tab === "pl" && (
        <div className="ra-card"><div className="ra-card-h"><i className="ti ti-chart-bar" aria-hidden="true" /> Income Statement (₹ Lacs)</div>
          <FinancialTable rows={PL_ROWS} raw={project.pl} computed={plComputed} onChange={setPlCell} fyLabels={fyLabels} testidPrefix="vs-pl" /></div>
      )}
      {tab === "bs" && (
        <div className="ra-card"><div className="ra-card-h"><i className="ti ti-stack-2" aria-hidden="true" /> Balance Sheet (₹ Lacs)</div>
          <FinancialTable rows={BS_ROWS} raw={project.bs} computed={bsComputed} onChange={setBsCell} fyLabels={fyLabels} testidPrefix="vs-bs" /></div>
      )}
      {tab === "assumptions" && (
        <div className="ra-card">
          <div className="ra-card-h"><i className="ti ti-calculator" aria-hidden="true" /> DCF Assumptions <span className="ra-card-sub">decimals — 0.12 = 12%. Blank = auto-suggested historical average.</span></div>
          {ASSUMPTION_ROWS.map((row) => {
            const stored = project.assumptions?.[row.id];
            const isAuto = stored == null || stored === "";
            const autoVal = isAuto && auto ? auto[row.id] : null;
            const display = stored ?? row.default ?? autoVal ?? "";
            return (
              <div key={row.id} className="vs-asm-row">
                <div className="vs-asm-label">
                  <div>{row.label}</div>
                  <div className="vs-asm-reason"><i className="ti ti-info-circle" aria-hidden="true" /> {row.reasoning}</div>
                </div>
                <div className="vs-asm-input">
                  <input type="number" step={row.integer ? 1 : "any"} className="vs-cell"
                    value={display === null ? "" : display}
                    onChange={(e) => setAssumption(row.id, e.target.value === "" ? null : Number(e.target.value))}
                    data-testid={`vs-asm-${row.id}`} />
                </div>
                <div className="vs-asm-hint">
                  {autoVal != null && isAuto && (
                    <span>Auto: <strong>{["ebitda_margin_steady", "depreciation_pct", "capex_pct", "wc_change_pct"].includes(row.id) ? `${(autoVal * 100).toFixed(2)}%` : (row.id === "projection_years" ? autoVal : autoVal.toFixed(4))}</strong></span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="vs-action-bar">
        <div className="vs-action-note">Inputs auto-save as you type. When ready, open the live valuation engine.</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="f-btn" onClick={onBack}>Back to Projects</button>
          <button className="f-btn solid" onClick={saveAndOpen} disabled={saving} data-testid="vs-save-open-engine"><i className="ti ti-device-floppy" aria-hidden="true" /> Save &amp; Open Valuation Engine <i className="ti ti-arrow-right" aria-hidden="true" /></button>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════
 * Engine view — live DCF / NAV / Comparable + weighted summary
 * ════════════════════════════════════════════════════════════════════════ */
const ENGINE_DEFAULTS = {
  company_name: "", sector_id: "edtech_training", shares_lakhs: 1.5,
  revenue: { fy1: 852.11, fy2: 937.32, fy3: 979.93 },
  ebitda: { fy1: 503.05, fy2: 552.03, fy3: 554.76 },
  pat_fy3: 409.11, total_assets_fy3: 1605.74, total_debt_fy3: 72.0, cash_fy3: 64.63,
  shareholders_equity_fy3: 1201.49, net_fixed_assets_fy3: 1267.0,
  accounts_receivable: 220.5, other_current_assets: 42.0, accounts_payable: 201.0,
  accrued_expenses: 131.0, long_term_debt: 72.0, intangible_brand: 200, goodwill: 150,
  wacc: 0.12, revenue_cagr: 0.15, ebitda_margin_steady: 0.55, depreciation_pct: 0.0157,
  capex_pct: 0.2, wc_change_pct: 0.02, tax_rate: 0.25, terminal_growth: 0.04, projection_years: 5,
  ev_ebitda_multiple: 10, ev_revenue_multiple: 3, pe_multiple: 15, apply_unlisted_discount: true,
  weight_dcf: 0.5, weight_nav: 0.25, weight_comparable: 0.25,
};

const EngineField = ({ label, value, onChange, suffix, hint, testid, step = "any" }) => (
  <div className="ra-field">
    <label className="ra-label">{label}</label>
    <div className="ra-input-wrap">
      <input type="number" step={step} className="ra-input" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={testid} />
      {suffix && <span className="ra-suffix">{suffix}</span>}
    </div>
    {hint && <div className="vs-asm-reason">{hint}</div>}
  </div>
);

const EngineView = ({ projectId, onBack, onEditInputs }) => {
  const [inp, setInp] = useState(ENGINE_DEFAULTS);
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("company");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/bv-projects/${projectId}`);
        if (cancelled) return;
        setProject(res.data);
        setInp((prev) => ({ ...prev, ...bvProjectToEngineInputs(res.data) }));
      } catch (e) { console.warn("BV project not found", e); }
    })();
    return () => { cancelled = true; };
  }, [projectId]); // eslint-disable-line

  const set = (path, value) => setInp((prev) => {
    if (path.includes(".")) { const [a, b] = path.split("."); return { ...prev, [a]: { ...prev[a], [b]: value } }; }
    return { ...prev, [path]: value };
  });

  const persistEngineConfig = useCallback(async (patch) => {
    if (!projectId) return;
    try { await api.put(`/bv-projects/${projectId}`, { engine_config: { ...(project?.engine_config || {}), ...patch } }); } catch { /* best-effort */ }
  }, [projectId, project]);

  const applySectorMultiples = (sector_id) => {
    const s = SECTOR_PEER_MULTIPLES.find((x) => x.id === sector_id);
    if (!s) return;
    setInp((p) => ({ ...p, sector_id, ev_ebitda_multiple: s.ev_ebitda, ev_revenue_multiple: s.ev_revenue, pe_multiple: s.pe }));
    persistEngineConfig({ sector_id, ev_ebitda_multiple: s.ev_ebitda, ev_revenue_multiple: s.ev_revenue, pe_multiple: s.pe });
  };

  const sector = SECTOR_PEER_MULTIPLES.find((s) => s.id === inp.sector_id) || SECTOR_PEER_MULTIPLES[0];
  const dcf = useMemo(() => calculateDCF(inp), [inp]);
  const nav = useMemo(() => calculateNAV(inp), [inp]);
  const cmp = useMemo(() => calculateComparable(inp), [inp]);
  const summary = useMemo(() => weightedSummary(dcf, nav, cmp, {
    weight_dcf: inp.weight_dcf, weight_nav: inp.weight_nav, weight_comparable: inp.weight_comparable, shares_lakhs: inp.shares_lakhs,
  }), [dcf, nav, cmp, inp.weight_dcf, inp.weight_nav, inp.weight_comparable, inp.shares_lakhs]);

  // Board / merchant-banker-ready PDF — structured text report with the
  // disclaimer repeated on every page footer. (helvetica has no ₹ glyph → "Rs ")
  const downloadReport = () => {
    try {
      const pdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 40;
      const FOOT = 56;
      let y = margin;
      const Rs = (v) => `Rs ${fmtL(v)}`;
      const ensure = (need) => { if (y + need > pageH - FOOT) { pdf.addPage(); y = margin; } };
      const sectionTitle = (t) => {
        ensure(30); y += 8;
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(12); pdf.setTextColor(20, 20, 45);
        pdf.text(t, margin, y); y += 6;
        pdf.setDrawColor(220, 220, 220); pdf.line(margin, y, pageW - margin, y); y += 13;
      };
      const kv = (label, value, bold = false) => {
        ensure(14);
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(9.5); pdf.setTextColor(80, 80, 80);
        pdf.text(label, margin, y);
        pdf.setFont("helvetica", bold ? "bold" : "normal"); pdf.setTextColor(20, 20, 20);
        pdf.text(String(value), pageW - margin, y, { align: "right" }); y += 14;
      };

      // Title block
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(18); pdf.setTextColor(15, 15, 15);
      pdf.text("Business Valuation Report", margin, y); y += 20;
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(12); pdf.setTextColor(60, 60, 60);
      pdf.text(project?.company_name || "Untitled BV Project", margin, y); y += 15;
      pdf.setFontSize(9.5); pdf.setTextColor(110, 110, 110);
      pdf.text(`Sector: ${sector.label}    |    All figures in Rs Lacs    |    Date: ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`, margin, y); y += 16;

      // Headline indicative value
      ensure(64);
      pdf.setFillColor(244, 246, 251); pdf.setDrawColor(214, 224, 240);
      pdf.roundedRect(margin, y, pageW - margin * 2, 56, 6, 6, "FD");
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(110, 110, 110);
      pdf.text("INDICATIVE WEIGHTED EQUITY VALUE", margin + 16, y + 20);
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(22); pdf.setTextColor(20, 20, 40);
      pdf.text(Rs(summary.weighted_equity_value), margin + 16, y + 44);
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(9.5); pdf.setTextColor(90, 90, 90);
      pdf.text(`Per share: ${Rs(summary.weighted_per_share)} / Lac share`, pageW - margin - 16, y + 44, { align: "right" });
      y += 56 + 6;

      // Weighted method summary
      sectionTitle("Weighted Method Summary");
      ensure(14);
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(8.5); pdf.setTextColor(90, 90, 90);
      pdf.text("Method", margin, y);
      pdf.text("Weight", margin + 250, y, { align: "right" });
      pdf.text("Equity (Rs L)", margin + 370, y, { align: "right" });
      pdf.text("Weighted (Rs L)", pageW - margin, y, { align: "right" }); y += 12;
      summary.rows.forEach((r) => {
        ensure(13);
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(50, 50, 50);
        pdf.text(r.method, margin, y);
        pdf.text(`${r.weight}%`, margin + 250, y, { align: "right" });
        pdf.text(fmtL(r.equity), margin + 370, y, { align: "right" });
        pdf.setFont("helvetica", "bold"); pdf.text(fmtL(r.weighted), pageW - margin, y, { align: "right" }); y += 13;
      });

      // DCF detail
      sectionTitle("DCF Method");
      kv("Equity Value", Rs(dcf.equity_value), true);
      kv("Per share", Rs(dcf.per_share));
      kv("Sum PV of FCFFs", Rs(dcf.sum_pv_fcff));
      kv("Terminal Value", Rs(dcf.terminal_value));
      kv("PV of Terminal Value", Rs(dcf.pv_terminal_value));
      kv("Enterprise Value", Rs(dcf.enterprise_value));
      kv("Less: Debt", Rs(dcf.less_debt));
      kv("Add: Cash", Rs(dcf.add_cash));

      // NAV detail
      sectionTitle("NAV Method (Adjusted Net Asset Value)");
      kv("Equity NAV", Rs(nav.equity_value), true);
      kv("Per share", Rs(nav.per_share));
      kv("Total Adjusted Assets", Rs(nav.total_adjusted_assets));
      kv("Total Liabilities", Rs(nav.total_liabilities));
      kv("Brand Intangible (add-back)", Rs(inp.intangible_brand));
      kv("Regulatory Goodwill (add-back)", Rs(inp.goodwill));

      // Comparable detail + peer set
      sectionTitle("Comparable Company Method");
      kv("Average Equity Value", Rs(cmp.average_equity_value), true);
      kv("Per share", Rs(cmp.per_share));
      cmp.sub_methods.forEach((m) => kv(`${m.name} (${m.multiple}x on ${fmtL(m.base)})`, Rs(m.equity)));
      if (inp.apply_unlisted_discount) kv("Unlisted discount applied", `${Math.round(UNLISTED_DISCOUNT * 100)}%`);
      ensure(16); y += 4;
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(70, 70, 70);
      pdf.text(`Peer Set (${buildPeerSet(inp.sector_id).length})`, margin, y); y += 13;
      buildPeerSet(inp.sector_id).forEach((peer) => kv(peer.name, peer.segment));

      // Key assumptions
      sectionTitle("Key Assumptions");
      const pct = (v) => `${((Number(v) || 0) * 100).toFixed(2)}%`;
      kv("WACC", pct(inp.wacc));
      kv("Revenue CAGR", pct(inp.revenue_cagr));
      kv("EBITDA Margin (steady)", pct(inp.ebitda_margin_steady));
      kv("Depreciation % of Revenue", pct(inp.depreciation_pct));
      kv("Capex % of Revenue", pct(inp.capex_pct));
      kv("ΔWorking Capital % of Revenue", pct(inp.wc_change_pct));
      kv("Tax Rate", pct(inp.tax_rate));
      kv("Terminal Growth Rate", pct(inp.terminal_growth));
      kv("Projection Period (Years)", inp.projection_years);
      kv("No. of shares (Lacs)", inp.shares_lakhs);

      // DCF projection table
      sectionTitle("DCF Projection (Rs Lacs)");
      const cols = dcf.projections;
      const labelW = 120;
      const colW = (pageW - margin * 2 - labelW) / Math.max(cols.length, 1);
      ensure(16);
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(8); pdf.setTextColor(60, 60, 60);
      pdf.text("Line Item", margin, y);
      cols.forEach((p, i) => pdf.text(p.year_label, margin + labelW + colW * (i + 1) - 4, y, { align: "right" }));
      y += 11;
      [["Revenue", "revenue"], ["EBITDA", "ebitda"], ["D&A", "da"], ["EBIT", "ebit"], ["Tax", "tax"], ["NOPAT", "nopat"], ["Capex", "capex"], ["Change in WC", "change_wc"], ["FCFF", "fcff"], ["Discount Factor", "discount_factor"], ["PV of FCFF", "pv_fcff"]].forEach(([label, key]) => {
        ensure(12);
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(70, 70, 70);
        pdf.text(label, margin, y);
        cols.forEach((p, i) => {
          const v = key === "discount_factor" ? p[key].toFixed(4) : fmtL(p[key]);
          pdf.text(String(v), margin + labelW + colW * (i + 1) - 4, y, { align: "right" });
        });
        y += 12;
      });

      // Disclaimer on EVERY page footer
      const total = pdf.getNumberOfPages();
      const discLines = pdf.splitTextToSize(DISCLAIMER, pageW - margin * 2);
      for (let i = 1; i <= total; i += 1) {
        pdf.setPage(i);
        pdf.setDrawColor(225, 225, 225); pdf.line(margin, pageH - FOOT + 8, pageW - margin, pageH - FOOT + 8);
        pdf.setFont("helvetica", "italic"); pdf.setFontSize(7.5); pdf.setTextColor(115, 115, 115);
        pdf.text(discLines, margin, pageH - FOOT + 22);
        pdf.setFont("helvetica", "normal"); pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${i} of ${total}`, pageW - margin, pageH - 14, { align: "right" });
      }

      pdf.save(`BV_Report_${(project?.company_name || "company").replace(/[^a-z0-9]+/gi, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("Valuation report downloaded.");
    } catch (e) {
      console.error(e);
      toast.error("Could not generate the report. Please retry.");
    }
  };

  return (
    <div data-testid="vs-engine-view">
      <div className="vs-subbar">
        <button className="f-btn" onClick={onBack} data-testid="vs-engine-back"><i className="ti ti-arrow-left" aria-hidden="true" /> Projects</button>
        <div className="vs-subtitle">{project?.company_name || "Business Valuation Engine"} <span>· DCF · NAV · Comparable in parallel · ₹ Lacs</span></div>
        <button className="f-btn solid" onClick={downloadReport} data-testid="vs-download-report"><i className="ti ti-download" aria-hidden="true" /> Download Valuation Report (PDF)</button>
        <button className="f-btn" onClick={() => onEditInputs(projectId)} data-testid="vs-edit-inputs"><i className="ti ti-edit" aria-hidden="true" /> Edit Inputs</button>
      </div>

      {/* Input tabs */}
      <div className="vs-tabs" data-testid="vs-engine-tabs">
        {[["company", "Company"], ["financials", "Financials"], ["balance", "Balance Sheet"], ["dcf", "DCF Assumptions"], ["comp", "Comparables"]].map(([k, l]) => (
          <button key={k} className={`vs-tab ${tab === k ? "on" : ""}`} onClick={() => setTab(k)} data-testid={`vs-etab-${k}`}>{l}</button>
        ))}
      </div>

      <div className="ra-card">
        {tab === "company" && (
          <div className="ra-grid-3">
            <div className="ra-field" style={{ gridColumn: "span 2" }}>
              <label className="ra-label">Sector (auto-selects peer multiples)</label>
              <select className="ra-input" value={inp.sector_id} onChange={(e) => applySectorMultiples(e.target.value)} data-testid="vs-sector-select">
                {SECTOR_PEER_MULTIPLES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <EngineField label="No. of shares (in Lacs)" value={inp.shares_lakhs} onChange={(v) => set("shares_lakhs", v)} testid="vs-shares" />
          </div>
        )}
        {tab === "financials" && (
          <div className="ra-grid-3">
            <EngineField label="Revenue FY 2025-26" value={inp.revenue.fy3} onChange={(v) => set("revenue.fy3", v)} testid="vs-rev-fy3" />
            <EngineField label="Revenue FY 2024-25" value={inp.revenue.fy2} onChange={(v) => set("revenue.fy2", v)} testid="vs-rev-fy2" />
            <EngineField label="Revenue FY 2023-24" value={inp.revenue.fy1} onChange={(v) => set("revenue.fy1", v)} testid="vs-rev-fy1" />
            <EngineField label="EBITDA FY 2025-26" value={inp.ebitda.fy3} onChange={(v) => set("ebitda.fy3", v)} testid="vs-eb-fy3" />
            <EngineField label="EBITDA FY 2024-25" value={inp.ebitda.fy2} onChange={(v) => set("ebitda.fy2", v)} testid="vs-eb-fy2" />
            <EngineField label="EBITDA FY 2023-24" value={inp.ebitda.fy1} onChange={(v) => set("ebitda.fy1", v)} testid="vs-eb-fy1" />
            <EngineField label="PAT FY 2025-26" value={inp.pat_fy3} onChange={(v) => set("pat_fy3", v)} testid="vs-pat" />
            <EngineField label="Total Assets FY 2025-26" value={inp.total_assets_fy3} onChange={(v) => set("total_assets_fy3", v)} testid="vs-ta" />
            <EngineField label="Shareholders' Equity FY 2025-26" value={inp.shareholders_equity_fy3} onChange={(v) => set("shareholders_equity_fy3", v)} testid="vs-se" />
          </div>
        )}
        {tab === "balance" && (
          <div className="ra-grid-3">
            <EngineField label="Cash & Cash Equivalents" value={inp.cash_fy3} onChange={(v) => set("cash_fy3", v)} testid="vs-cash" />
            <EngineField label="Accounts Receivable" value={inp.accounts_receivable} onChange={(v) => set("accounts_receivable", v)} hint="5% recoverability haircut applied" testid="vs-ar" />
            <EngineField label="Other Current Assets" value={inp.other_current_assets} onChange={(v) => set("other_current_assets", v)} testid="vs-oca" />
            <EngineField label="Net Fixed Assets" value={inp.net_fixed_assets_fy3} onChange={(v) => set("net_fixed_assets_fy3", v)} testid="vs-nfa" />
            <EngineField label="Total Debt" value={inp.total_debt_fy3} onChange={(v) => set("total_debt_fy3", v)} testid="vs-debt" />
            <EngineField label="Long-term Debt (NAV)" value={inp.long_term_debt} onChange={(v) => set("long_term_debt", v)} testid="vs-ltd" />
            <EngineField label="Accounts Payable" value={inp.accounts_payable} onChange={(v) => set("accounts_payable", v)} testid="vs-ap" />
            <EngineField label="Accrued Expenses" value={inp.accrued_expenses} onChange={(v) => set("accrued_expenses", v)} testid="vs-accr" />
            <EngineField label="Brand Intangible (NAV add-back)" value={inp.intangible_brand} onChange={(v) => set("intangible_brand", v)} hint="AI-suggested intangible value" testid="vs-brand" />
            <EngineField label="Regulatory Goodwill (NAV add-back)" value={inp.goodwill} onChange={(v) => set("goodwill", v)} hint="AI-suggested strategic goodwill" testid="vs-gw" />
          </div>
        )}
        {tab === "dcf" && (
          <div className="ra-grid-3">
            <EngineField label="WACC" value={inp.wacc} onChange={(v) => set("wacc", v)} suffix="dec" testid="vs-wacc" />
            <EngineField label="Revenue CAGR" value={inp.revenue_cagr} onChange={(v) => set("revenue_cagr", v)} suffix="dec" testid="vs-cagr" />
            <EngineField label="EBITDA Margin (steady)" value={inp.ebitda_margin_steady} onChange={(v) => set("ebitda_margin_steady", v)} suffix="dec" testid="vs-margin" />
            <EngineField label="Depreciation % of Revenue" value={inp.depreciation_pct} onChange={(v) => set("depreciation_pct", v)} suffix="dec" testid="vs-dpct" />
            <EngineField label="Capex % of Revenue" value={inp.capex_pct} onChange={(v) => set("capex_pct", v)} suffix="dec" testid="vs-capexpct" />
            <EngineField label="ΔWorking Capital % of Revenue" value={inp.wc_change_pct} onChange={(v) => set("wc_change_pct", v)} suffix="dec" testid="vs-wcpct" />
            <EngineField label="Tax Rate" value={inp.tax_rate} onChange={(v) => set("tax_rate", v)} suffix="dec" hint="Sec 115BAB / effective" testid="vs-tax" />
            <EngineField label="Terminal Growth Rate" value={inp.terminal_growth} onChange={(v) => set("terminal_growth", v)} suffix="dec" testid="vs-tg" />
            <EngineField label="Projection Period (Years)" value={inp.projection_years} onChange={(v) => set("projection_years", v)} testid="vs-years" />
          </div>
        )}
        {tab === "comp" && (
          <>
            <div className="ra-grid-3">
              <EngineField label="EV/EBITDA Multiple" value={inp.ev_ebitda_multiple} onChange={(v) => set("ev_ebitda_multiple", v)} suffix="x" testid="vs-evebitda" />
              <EngineField label="EV/Revenue Multiple" value={inp.ev_revenue_multiple} onChange={(v) => set("ev_revenue_multiple", v)} suffix="x" testid="vs-evrev" />
              <EngineField label="P/E Multiple" value={inp.pe_multiple} onChange={(v) => set("pe_multiple", v)} suffix="x" testid="vs-pe" />
            </div>
            <label className="vs-checkbox">
              <input type="checkbox" checked={inp.apply_unlisted_discount} onChange={(e) => set("apply_unlisted_discount", e.target.checked)} data-testid="vs-discount-toggle" />
              Apply {Math.round(UNLISTED_DISCOUNT * 100)}% unlisted-discount on listed peer multiples (NSE/BSE-derived)
            </label>
            <div className="vs-asm-reason" style={{ marginTop: 8 }}><i className="ti ti-info-circle" aria-hidden="true" /> Defaults are median multiples for the chosen sector — derived from listed NSE/BSE peers. Override if you have a tighter peer set.</div>
          </>
        )}
      </div>

      {/* Method weights */}
      <div className="ra-card">
        <div className="ra-card-h"><i className="ti ti-adjustments" aria-hidden="true" /> Method Weights <span className="ra-card-sub">decimals — sum to 1.0</span></div>
        <div className="ra-grid-3">
          <EngineField label="DCF weight" value={inp.weight_dcf} onChange={(v) => set("weight_dcf", v)} testid="vs-w-dcf" />
          <EngineField label="NAV weight" value={inp.weight_nav} onChange={(v) => set("weight_nav", v)} testid="vs-w-nav" />
          <EngineField label="Comparable Co. weight" value={inp.weight_comparable} onChange={(v) => set("weight_comparable", v)} testid="vs-w-cmp" />
        </div>
      </div>

      {/* Output */}
      <div className="ra-sec-title" style={{ marginTop: 18 }}>Output — Live Valuation <span className="ra-card-sub" style={{ marginLeft: 6 }}>recomputed on every input change</span></div>
      <div className="vs-out-grid" data-testid="vs-output-grid">
        {/* DCF */}
        <div className="vs-out-card vs-dcf" data-testid="vs-card-dcf">
          <div className="vs-out-h" style={{ color: "#3C3489" }}><i className="ti ti-calculator" aria-hidden="true" /> DCF Method <span className="vs-out-w">Weight {Math.round(inp.weight_dcf * 100)}%</span></div>
          <div className="vs-out-big">₹ {fmtL(dcf.equity_value)} <span style={{ fontSize: 14, color: "var(--color-text-tertiary)" }}>L</span></div>
          <div className="vs-out-sub">Equity Value · per share ₹ {fmtL(dcf.per_share)} L</div>
          <div className="vs-out-rows">
            <div className="r"><span>Sum PV of FCFFs</span><span>₹ {fmtL(dcf.sum_pv_fcff)}</span></div>
            <div className="r"><span>Terminal Value</span><span>₹ {fmtL(dcf.terminal_value)}</span></div>
            <div className="r"><span>PV of Terminal</span><span>₹ {fmtL(dcf.pv_terminal_value)}</span></div>
            <div className="r"><span>Enterprise Value</span><span>₹ {fmtL(dcf.enterprise_value)}</span></div>
            <div className="r"><span>Less: Debt</span><span>₹ {fmtL(dcf.less_debt)}</span></div>
            <div className="r"><span>Add: Cash</span><span>₹ {fmtL(dcf.add_cash)}</span></div>
          </div>
          <div className="vs-out-note">Ref: DCF per RBI FDI Pricing Master Directions; Sec. 56(2)(x) IT Act for unlisted shares.</div>
        </div>
        {/* NAV */}
        <div className="vs-out-card vs-nav" data-testid="vs-card-nav">
          <div className="vs-out-h" style={{ color: "#0F6E56" }}><i className="ti ti-stack-2" aria-hidden="true" /> NAV Method <span className="vs-out-w">Weight {Math.round(inp.weight_nav * 100)}%</span></div>
          <div className="vs-out-big">₹ {fmtL(nav.equity_value)} <span style={{ fontSize: 14, color: "var(--color-text-tertiary)" }}>L</span></div>
          <div className="vs-out-sub">Equity NAV · per share ₹ {fmtL(nav.per_share)} L</div>
          <div className="vs-out-rows">
            <div className="r"><span>Total Adjusted Assets</span><span>₹ {fmtL(nav.total_adjusted_assets)}</span></div>
            <div className="r"><span>Total Liabilities</span><span>₹ {fmtL(nav.total_liabilities)}</span></div>
            <div className="r"><span>Brand Intangible</span><span>₹ {fmtL(inp.intangible_brand)}</span></div>
            <div className="r"><span>Regulatory Goodwill</span><span>₹ {fmtL(inp.goodwill)}</span></div>
          </div>
          <div className="vs-out-note">Ref: Rule 11UA, IT Rules 1962. Conservative floor value with brand &amp; goodwill add-backs.</div>
        </div>
        {/* Comparable */}
        <div className="vs-out-card vs-comp" data-testid="vs-card-comp">
          <div className="vs-out-h" style={{ color: "#BA7517" }}><i className="ti ti-chart-bar" aria-hidden="true" /> Comparable Co. <span className="vs-out-w">Weight {Math.round(inp.weight_comparable * 100)}%</span></div>
          <div className="vs-out-big">₹ {fmtL(cmp.average_equity_value)} <span style={{ fontSize: 14, color: "var(--color-text-tertiary)" }}>L</span></div>
          <div className="vs-out-sub">Avg Equity Value · per share ₹ {fmtL(cmp.per_share)} L</div>
          <div className="vs-out-rows">
            {cmp.sub_methods.map((m) => (
              <div key={m.name} className="r"><span>{m.name} ({m.multiple}x on {fmtL(m.base)})</span><span>₹ {fmtL(m.equity)}</span></div>
            ))}
            {inp.apply_unlisted_discount && <div style={{ fontSize: 10, color: "#854F0B", fontStyle: "italic", marginTop: 4 }}>{Math.round(UNLISTED_DISCOUNT * 100)}% unlisted discount applied to peer multiples.</div>}
          </div>
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: ".5px solid var(--color-border-tertiary)" }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".1em", color: "#854F0B", fontWeight: 600, marginBottom: 4 }}>Peer Set ({buildPeerSet(inp.sector_id).length})</div>
            {buildPeerSet(inp.sector_id).map((peer) => (
              <div key={peer.name} className="vs-peer-row"><span style={{ color: "var(--color-text-secondary)" }}>{peer.name}</span><span style={{ color: peer.segment === "Large Cap" ? "#0F6E56" : peer.segment === "Mid Cap" ? "#854F0B" : "#3C3489" }}>{peer.segment}</span></div>
            ))}
          </div>
          <div className="vs-out-note">3 Large Cap + 1 Mid Cap + Broad NIFTY-500 sector median (NSE/BSE-derived).</div>
        </div>
      </div>

      {/* Weighted summary */}
      <div className="vs-summary" data-testid="vs-summary">
        <div className="ra-card-h" style={{ background: "none", border: "none", marginBottom: 12 }}><i className="ti ti-sparkles" aria-hidden="true" /> Weighted Equity Value (Indicative)</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 22, alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".15em", color: "var(--color-text-tertiary)", marginBottom: 4 }}>Indicative Equity</div>
            <div className="vs-summary-big">₹ {fmtL(summary.weighted_equity_value)} <span style={{ fontSize: 18, color: "var(--color-text-tertiary)" }}>L</span></div>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 4 }}>Per share: ₹ {fmtL(summary.weighted_per_share)} L / Lac share</div>
          </div>
          <table className="vs-sum-table">
            <thead><tr><th>Method</th><th>Weight</th><th>Equity (₹ L)</th><th>Weighted (₹ L)</th></tr></thead>
            <tbody>
              {summary.rows.map((r) => (
                <tr key={r.method}><td>{r.method}</td><td>{r.weight}%</td><td>{fmtL(r.equity)}</td><td><strong>{fmtL(r.weighted)}</strong></td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="vs-out-note" style={{ marginTop: 12 }}>Indicative valuation — not investment, legal or tax advice. DCF preferred under RBI FDI Pricing Master Directions. NAV per Rule 11UA, IT Rules 1962. Comparable method uses listed-peer multiples with an unlisted discount.</div>
      </div>

      {/* DCF projection table */}
      <div className="ra-card" style={{ marginTop: 14 }}>
        <div className="ra-card-h"><i className="ti ti-calculator" aria-hidden="true" /> DCF Projection (₹ Lacs)</div>
        <div style={{ overflowX: "auto" }}>
          <table className="vs-fin-table" data-testid="vs-dcf-projection">
            <thead><tr><th style={{ textAlign: "left" }}>Line Item</th>{dcf.projections.map((p) => <th key={p.year_label}>{p.year_label}</th>)}</tr></thead>
            <tbody>
              {[["Revenue", "revenue"], ["EBITDA", "ebitda"], ["D&A", "da"], ["EBIT", "ebit"], ["Tax", "tax"], ["NOPAT", "nopat"], ["Capex", "capex"], ["ΔWC", "change_wc"], ["FCFF", "fcff"], ["Discount Factor", "discount_factor"], ["PV of FCFF", "pv_fcff"]].map(([label, key]) => (
                <tr key={key} className="vs-fin-row">
                  <td className="vs-fin-label">{label}</td>
                  {dcf.projections.map((p) => <td key={p.year_label} style={{ textAlign: "right" }}>{key === "discount_factor" ? p[key].toFixed(4) : fmtL(p[key])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="vs-disclaimer">
        <i className="ti ti-shield" style={{ fontSize: 14, color: "#BA7517" }} aria-hidden="true" />
        <div className="t">{DISCLAIMER}</div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════
 * Parent
 * ════════════════════════════════════════════════════════════════════════ */
const ValuationStudioDesk = () => {
  const [view, setView] = useState("projects"); // projects | inputs | engine
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const autoOpened = useRef(false);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get("/bv-projects"); setProjects(res.data?.projects || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // Honour "clicking Valuation Studio opens the Start-a-new-BV popup": auto-open
  // the create dialog once after the first load when below the project cap.
  useEffect(() => {
    if (loading || autoOpened.current) return;
    autoOpened.current = true;
    if (projects.length < MAX_PROJECTS) setShowCreate(true);
  }, [loading, projects.length]);

  const onCreated = (proj) => {
    setShowCreate(false);
    setProjects((p) => [proj, ...p]);
    setSelectedId(proj.project_id);
    setView("inputs");
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/bv-projects/${confirmDelete.project_id}`);
      toast.success("Project moved to audit log (30-day retention)");
      setConfirmDelete(null);
      fetchProjects();
    } catch { toast.error("Failed to delete"); }
  };

  const backToProjects = () => { setView("projects"); setSelectedId(null); fetchProjects(); };

  return (
    <div className="fp" data-testid="valuation-studio-desk">
      <div className="fp-bar">
        <div className="fp-title">Valuation <span>Studio</span></div>
        {view === "projects" && (
          <button className="fp-pill pri" onClick={() => setShowCreate(true)} disabled={projects.length >= MAX_PROJECTS} data-testid="vs-start-new">
            <i className="ti ti-plus" aria-hidden="true" /> Start a new Business Valuation
          </button>
        )}
      </div>

      <div className="fp-body">
        {view === "projects" && (
          <>
            <p className="vs-tagline" data-testid="vs-tagline">
              <b>Three Valuations Techniques. One Engine.</b> Capture your P&amp;L, Balance Sheet and DCF
              assumptions once — the BV Engine runs DCF, NAV and Comparable Company valuations in
              parallel, with a sector-aware peer set.
            </p>
            <ProjectsView
              projects={projects}
              loading={loading}
              onOpenCreate={() => setShowCreate(true)}
              onOpenInputs={(id) => { setSelectedId(id); setView("inputs"); }}
              onOpenEngine={(id) => { setSelectedId(id); setView("engine"); }}
              onDelete={(p) => setConfirmDelete(p)}
            />
          </>
        )}
        {view === "inputs" && selectedId && (
          <InputsView projectId={selectedId} onBack={backToProjects} onOpenEngine={(id) => { setSelectedId(id); setView("engine"); }} />
        )}
        {view === "engine" && selectedId && (
          <EngineView projectId={selectedId} onBack={backToProjects} onEditInputs={(id) => { setSelectedId(id); setView("inputs"); }} />
        )}
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={onCreated} />}

      {confirmDelete && (
        <div className="vs-modal-overlay" data-testid="vs-delete-modal">
          <div className="vs-modal" style={{ width: 420 }}>
            <div className="vs-modal-head"><div className="vs-modal-title">Delete project?</div><button className="rf-quiz-x" onClick={() => setConfirmDelete(null)}><i className="ti ti-x" aria-hidden="true" /></button></div>
            <div className="vs-modal-body">
              <p className="vs-tagline" style={{ margin: 0 }}><b>{confirmDelete.company_name || "Untitled"}</b> moves to the audit log. Recoverable for <b>30 days</b>, then permanently deleted.</p>
            </div>
            <div className="vs-modal-foot">
              <button className="f-btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="f-btn solid" style={{ background: "#A32D2D", borderColor: "#A32D2D" }} onClick={doDelete} data-testid="vs-confirm-delete">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValuationStudioDesk;
